import axios, { AxiosError } from "axios";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { env } from "./env";
import type {
  ApiError,
  RefreshTokenResponse,
  SendOtpResponse,
  VerifyOtpResponse,
} from "../types/api";
import { forceLogoutAndRedirect } from "../stores/authStore";
import { captureException } from "../config/sentry";

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

const getRecord = (value: unknown): Record<string, unknown> | null => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
};

const firstStringValue = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim()) {
    return value;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const result = firstStringValue(item);
      if (result) {
        return result;
      }
    }
  }

  const record = getRecord(value);
  if (record) {
    for (const item of Object.values(record)) {
      const result = firstStringValue(item);
      if (result) {
        return result;
      }
    }
  }

  return undefined;
};

const META_ERROR_KEYS = new Set([
  "code",
  "message",
  "status",
  "retry_after_seconds",
]);

// DRF returns field validation errors as a flat object — {"email": ["..."]} —
// with no wrapping `errors`/`detail`/`details` key. Treat that shape as field
// errors too, otherwise forms get an undefined `details` and show nothing.
const flatFieldErrors = (
  record: Record<string, unknown>
): Record<string, unknown> | undefined => {
  const entries = Object.entries(record).filter(
    ([key, value]) =>
      !META_ERROR_KEYS.has(key) &&
      (typeof value === "string" || Array.isArray(value))
  );

  return entries.length ? Object.fromEntries(entries) : undefined;
};

const normalizeApiError = (
  error: unknown,
  fallbackMessage: string
): ApiError => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const record = getRecord(data);
    const details =
      record?.errors ??
      record?.detail ??
      record?.details ??
      (record ? flatFieldErrors(record) : undefined);
    const message =
      firstStringValue(record?.message) ??
      firstStringValue(record?.error) ??
      firstStringValue(details) ??
      error.message ??
      fallbackMessage;
    const retryAfterSeconds = Number(record?.retry_after_seconds);

    return {
      message,
      status: error.response?.status || 0,
      code: typeof record?.code === "string" ? record.code : undefined,
      details,
      retry_after_seconds: Number.isFinite(retryAfterSeconds)
        ? retryAfterSeconds
        : undefined,
    };
  }

  if (error instanceof Error && error.message) {
    return {
      message: error.message,
      status: 0,
    };
  }

  const record = getRecord(error);
  if (record) {
    const retryAfterSeconds = Number(record.retry_after_seconds);

    return {
      message: firstStringValue(record.message) ?? fallbackMessage,
      status: typeof record.status === "number" ? record.status : 0,
      code: typeof record.code === "string" ? record.code : undefined,
      details: record.details,
      retry_after_seconds: Number.isFinite(retryAfterSeconds)
        ? retryAfterSeconds
        : undefined,
    };
  }

  return {
    message: fallbackMessage,
    status: 0,
  };
};

export const getApiErrorStatus = (error: unknown): number => {
  if (axios.isAxiosError(error)) {
    return error.response?.status ?? 0;
  }

  const record = getRecord(error);
  return typeof record?.status === "number" ? record.status : 0;
};

export const getApiErrorCode = (error: unknown): string | undefined => {
  if (axios.isAxiosError(error)) {
    const record = getRecord(error.response?.data);
    return typeof record?.code === "string" ? record.code : undefined;
  }

  const record = getRecord(error);
  return typeof record?.code === "string" ? record.code : undefined;
};

export const getApiErrorMessage = (
  error: unknown,
  fallbackMessage: string
): string => {
  if (axios.isAxiosError(error)) {
    return (
      firstStringValue(error.response?.data) || error.message || fallbackMessage
    );
  }

  const record = getRecord(error);
  return firstStringValue(record?.message) ?? fallbackMessage;
};

// Token management utilities
class TokenManager {
  private static readonly ACCESS_TOKEN_KEY = "access_token";
  private static readonly REFRESH_TOKEN_KEY = "refresh_token";

  static getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }
}

// Request interceptor for JWT token attachment
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = TokenManager.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing the token to prevent multiple refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];
let hasRefreshFailed = false;

// Function to notify all subscribers when token is refreshed
const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Function to add subscribers waiting for token refresh
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Function to reset refresh state (call this on successful login)
const resetRefreshState = () => {
  hasRefreshFailed = false;
  isRefreshing = false;
  refreshSubscribers = [];
};

// Response interceptor for token refresh and error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If refresh has already failed in this session, don't attempt again
      if (hasRefreshFailed) {
        TokenManager.clearTokens();
        forceLogoutAndRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If we're already refreshing, wait for the new token
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token: string) => {
            if (token) {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(api(originalRequest));
            } else {
              // Refresh failed, reject the promise
              reject(error);
            }
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = TokenManager.getRefreshToken();

        if (!refreshToken) {
          // No refresh token available, mark as failed and redirect to login
          hasRefreshFailed = true;
          TokenManager.clearTokens();
          forceLogoutAndRedirect();
          return Promise.reject(error);
        }

        const response = await axios.post<RefreshTokenResponse>(
          `${env.API_BASE_URL}/users/refresh/`,
          { refresh_token: refreshToken }
        );

        const newAccessToken = response.data.token;

        if (newAccessToken) {
          const nextRefreshToken = response.data.refresh_token ?? refreshToken;

          // The user may have logged out or started a different session while
          // this refresh request was in flight. Never restore tokens from a
          // stale refresh response.
          if (TokenManager.getRefreshToken() !== refreshToken) {
            refreshSubscribers.forEach((callback) => callback(""));
            refreshSubscribers = [];
            return Promise.reject(error);
          }

          TokenManager.setTokens(newAccessToken, nextRefreshToken);
          onRefreshed(newAccessToken);

          // A current-user request already refreshes permissions when retried
          // below. For all other requests, refresh permissions in the
          // background so live RBAC changes are picked up after token renewal.
          if (originalRequest.url !== "/users/") {
            void import("../stores/authStore")
              .then(({ useAuthStore }) =>
                useAuthStore.getState().refreshCurrentUser()
              )
              .catch(() => {
                // Keep the successfully refreshed session. A later bootstrap
                // or API request can retry permission synchronization.
              });
          }

          // Retry the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }
          return api(originalRequest);
        } else {
          // No access token in response, mark as failed
          hasRefreshFailed = true;
          TokenManager.clearTokens();
          forceLogoutAndRedirect();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Refresh failed, mark as failed and clear tokens
        hasRefreshFailed = true;
        TokenManager.clearTokens();

        // Notify all waiting subscribers that refresh failed
        refreshSubscribers.forEach((callback) => callback(""));
        refreshSubscribers = [];

        forceLogoutAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const status = error.response?.status;
    if (status && status >= 500) {
      captureException(error, {
        url: originalRequest?.url,
        method: originalRequest?.method,
        status,
      });
    }

    return Promise.reject(error);
  }
);

// API response wrapper for consistent error handling
export const apiRequest = async <T>(
  requestFn: () => Promise<any>
): Promise<T> => {
  try {
    const response = await requestFn();
    return response.data;
  } catch (error) {
    throw normalizeApiError(error, "Request failed");
  }
};

// Retry logic for failed requests
export const withRetry = async <T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: Error;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error as Error;

      // Don't retry on authentication errors or client errors (4xx)
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status && status >= 400 && status < 500) {
          throw error;
        }
      }

      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, delay * attempt));
      }
    }
  }

  throw lastError!;
};

// OTP Authentication API functions
export const sendOtp = async (
  phoneNumber: string
): Promise<SendOtpResponse> => {
  try {
    const response = await axios.post<SendOtpResponse>(
      `${env.API_BASE_URL}/otp/send/`,
      { phone_number: phoneNumber, client: "console" },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    if (!response.data.otp_status) {
      throw {
        message: "Unable to send OTP right now. Please try again.",
        status: 0,
        code: "otp_delivery_failed",
      } satisfies ApiError;
    }
    return response.data;
  } catch (error) {
    throw normalizeApiError(error, "Failed to send OTP");
  }
};

export const verifyOtp = async (
  phoneNumber: string,
  otp: string
): Promise<VerifyOtpResponse> => {
  try {
    const response = await axios.post<VerifyOtpResponse>(
      `${env.API_BASE_URL}/otp/verify/`,
      { phone_number: phoneNumber, otp, platform: "console" },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return response.data;
  } catch (error) {
    throw normalizeApiError(error, "Failed to verify OTP");
  }
};

// Enhanced API service functions with better error handling
export const apiService = {
  // Auth
  sendOtp: (phoneNumber: string) => apiRequest(() => sendOtp(phoneNumber)),
  verifyOtp: (phoneNumber: string, otp: string) =>
    apiRequest(() => verifyOtp(phoneNumber, otp)),

  // Users
  getProfile: () => api.get("/user/profile"),
  updateProfile: (data: any) => api.put("/user/profile", data),

  // Conversations
  getConversations: () => api.get("/conversations"),
  getConversation: (id: string) => api.get(`/conversations/${id}`),
  sendMessage: (conversationId: string, message: string) =>
    api.post(`/conversations/${conversationId}/messages`, { message }),
  addNote: (conversationId: string, note: string) =>
    api.post(`/conversations/${conversationId}/notes`, { note }),

  // Vitals
  getBloodGlucoseSourceTypes: () =>
    api.get("/vitals/blood-glucose-source-type/"),
  getBloodPressurePositions: () => api.get("/vitals/blood-pressure-positions/"),
  getBloodPressureLocations: () => api.get("/vitals/blood-pressure-locations/"),
  getTemperatureLocations: () => api.get("/vitals/temperature-locations/"),
  getBloodGlucoseData: (
    patientId: string,
    startDate: string,
    endDate: string
  ) =>
    api.get(`/vitals/user/v2/blood-glucose/`, {
      params: {
        patient_id: patientId,
        reading_start: startDate,
        reading_end: endDate,
      },
    }),

  getBloodPressureData: (
    patientId: string,
    startDate: string,
    endDate: string
  ) =>
    api.get(`/vitals/user/v2/blood-pressure/`, {
      params: {
        patient_id: patientId,
        reading_start: startDate,
        reading_end: endDate,
      },
    }),

  getHeartRateData: (patientId: string, startDate: string, endDate: string) =>
    api.get(`/vitals/user/v2/heart-beat/`, {
      params: {
        patient_id: patientId,
        reading_start: startDate,
        reading_end: endDate,
      },
    }),

  getWaistSizeData: (patientId: string, startDate: string, endDate: string) =>
    api.get(`/patient/v2/waist-size/`, {
      params: {
        patient_id: patientId,
        reading_start: startDate,
        reading_end: endDate,
      },
    }),

  getHipSizeData: (patientId: string, startDate: string, endDate: string) =>
    api.get(`/patient/v2/hip-size/`, {
      params: {
        patient_id: patientId,
        reading_start: startDate,
        reading_end: endDate,
      },
    }),

  getWeightData: (patientId: string, startDate: string, endDate: string) =>
    api.get(`/patient/v2/weight/`, {
      params: {
        patient_id: patientId,
        reading_start: startDate,
        reading_end: endDate,
      },
    }),

  getRespiratoryRateData: (
    patientId: string,
    startDate: string,
    endDate: string
  ) =>
    api.get(`/vitals/user/v2/respiratory-rate/`, {
      params: {
        patient_id: patientId,
        reading_start: startDate,
        reading_end: endDate,
      },
    }),

  getTemperatureData: (patientId: string, startDate: string, endDate: string) =>
    api.get(`/vitals/user/v2/temperature/`, {
      params: {
        patient_id: patientId,
        reading_start: startDate,
        reading_end: endDate,
      },
    }),

  submitTemperatureReading: (data: {
    temperature: number;
    unit: "F";
    location: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/vitals/user/v2/temperature/", data),

  submitBloodPressureReading: (data: {
    systolic_bp: number;
    diastolic_bp: number;
    body_position: number;
    location: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/vitals/user/v2/blood-pressure/", data),

  submitBloodGlucoseReading: (data: {
    value: number;
    sample_unit: string;
    sample_type: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/vitals/user/v2/blood-glucose/", data),

  submitHeartRateReading: (data: {
    rate_value: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/vitals/user/v2/heart-beat/", data),

  submitWaistSizeReading: (data: {
    waist_size: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/patient/v2/waist-size/", data),

  submitHipSizeReading: (data: {
    hip_size: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/patient/v2/hip-size/", data),

  submitWeightReading: (data: {
    weight: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/patient/v2/weight/", data),

  submitRespiratoryRateReading: (data: {
    rate_value: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/vitals/user/v2/respiratory-rate/", data),

  // Validation APIs
  validateBloodGlucoseReading: (data: {
    value: number;
    sample_unit: string;
    sample_type: number;
    reading_datetime: string;
  }) => api.post("/vitals/validate/blood-glucose/", data),

  validateBloodPressureReading: (data: {
    systolic_bp: number;
    diastolic_bp: number;
    reading_datetime: string;
  }) => api.post("/vitals/validate/blood-pressure/", data),

  validateHeartRateReading: (data: {
    rate_value: number;
    reading_datetime: string;
  }) => api.post("/vitals/validate/heart-beat/", data),

  validateWeightReading: (data: {
    weight: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/patient/validate/weight/", data),

  validateWaistSizeReading: (data: {
    waist_size: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/patient/validate/waist-size/", data),

  validateHipSizeReading: (data: {
    hip_size: number;
    reading_datetime: string;
    patient_id: string;
  }) => api.post("/patient/validate/hip-size/", data),

  // Delete weight reading
  deleteWeightReading: (readingId: string, patientId: string) =>
    api.delete(`/patient/v2/weight/${readingId}/`, {
      params: {
        patient_id: patientId,
      },
    }),

  // Delete blood glucose reading
  deleteBloodGlucoseReading: (readingId: string, patientId: string) =>
    api.delete(`/vitals/user/v2/blood-glucose/${readingId}/`, {
      params: {
        patient_id: patientId,
      },
    }),

  // Delete blood pressure reading
  deleteBloodPressureReading: (readingId: string, patientId: string) =>
    api.delete(`/vitals/user/v2/blood-pressure/${readingId}/`, {
      params: {
        patient_id: patientId,
      },
    }),

  // Delete heart rate reading
  deleteHeartRateReading: (readingId: string, patientId: string) =>
    api.delete(`/vitals/user/v2/heart-beat/${readingId}/`, {
      params: {
        patient_id: patientId,
      },
    }),

  // Delete temperature reading
  deleteTemperatureReading: (readingId: string, patientId: string) =>
    api.delete(`/vitals/user/v2/temperature/${readingId}/`, {
      params: {
        patient_id: patientId,
      },
    }),

  // Delete respiratory rate reading
  deleteRespiratoryRateReading: (readingId: string, patientId: string) =>
    api.delete(`/vitals/user/v2/respiratory-rate/${readingId}/`, {
      params: {
        patient_id: patientId,
      },
    }),

  // Delete hip size reading
  deleteHipSizeReading: (readingId: string, patientId: string) =>
    api.delete(`/patient/v2/hip-size/${readingId}/`, {
      params: {
        patient_id: patientId,
      },
    }),

  // Delete waist size reading
  deleteWaistSizeReading: (readingId: string, patientId: string) =>
    api.delete(`/patient/v2/waist-size/${readingId}/`, {
      params: {
        patient_id: patientId,
      },
    }),

  // Get min/max values for all trackers (last 7 days)
  getTrackerMinMaxValues: (patientId: string) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 7);

    // Set start date to beginning of day and end date to end of day
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    const formatDate = (date: Date) => date.toISOString();

    return Promise.allSettled([
      // Blood Glucose
      api
        .get(`/vitals/user/v2/blood-glucose/`, {
          params: {
            patient_id: patientId,
            reading_start: formatDate(startDate),
            reading_end: formatDate(endDate),
          },
        })
        .then((response) => ({
          type: "blood-glucose",
          data: response.data.data || [],
        })),

      // Blood Pressure
      api
        .get(`/vitals/user/v2/blood-pressure/`, {
          params: {
            patient_id: patientId,
            reading_start: formatDate(startDate),
            reading_end: formatDate(endDate),
          },
        })
        .then((response) => ({
          type: "blood-pressure",
          data: response.data,
        })),

      // Heart Rate
      api
        .get(`/vitals/user/v2/heart-beat/`, {
          params: {
            patient_id: patientId,
            reading_start: formatDate(startDate),
            reading_end: formatDate(endDate),
          },
        })
        .then((response) => ({
          type: "heart-rate",
          data: response.data.data || [],
        })),

      // Waist Size
      api
        .get(`/patient/v2/waist-size/`, {
          params: {
            patient_id: patientId,
            reading_start: formatDate(startDate),
            reading_end: formatDate(endDate),
          },
        })
        .then((response) => ({
          type: "waist-size",
          data: response.data.data || [],
        })),

      // Hip Size
      api
        .get(`/patient/v2/hip-size/`, {
          params: {
            patient_id: patientId,
            reading_start: formatDate(startDate),
            reading_end: formatDate(endDate),
          },
        })
        .then((response) => ({
          type: "hip",
          data: response.data.data || [],
        })),

      // Weight
      api
        .get(`/patient/v2/weight/`, {
          params: {
            patient_id: patientId,
            reading_start: formatDate(startDate),
            reading_end: formatDate(endDate),
          },
        })
        .then((response) => ({
          type: "weight",
          data: response.data.data || [],
        })),

      // Respiratory Rate
      api
        .get(`/vitals/user/v2/respiratory-rate/`, {
          params: {
            patient_id: patientId,
            reading_start: formatDate(startDate),
            reading_end: formatDate(endDate),
          },
        })
        .then((response) => ({
          type: "respiratory-rate",
          data: response.data.data || [],
        })),

      // Temperature
      api
        .get(`/vitals/user/v2/temperature/`, {
          params: {
            patient_id: patientId,
            reading_start: formatDate(startDate),
            reading_end: formatDate(endDate),
          },
        })
        .then((response) => ({
          type: "temperature",
          data: response.data.data || [],
        })),
    ]);
  },

  // Medicine API
  getDoseForms: () => api.get("/medicines/v2/dose-forms/"),
  getMedicineUnits: () => api.get("/medicines/v2/unit/"),
  searchMedicines: (query: string, doseForm: string = "") => {
    const params = new URLSearchParams();
    params.append("search", query);
    if (doseForm) {
      params.append("dose_form", doseForm);
    }
    return api.get(`/medicines/v2/?${params.toString()}`);
  },
  searchAPIs: (query: string) =>
    api.get(`/medicines/v2/api/?search=${encodeURIComponent(query)}`),
  createAPI: (apiName: string) =>
    api.post("/medicines/v2/api/", { api_name: apiName }),
  addMedicine: (data: {
    name: string;
    medicine_unit: string;
    dose_form: string;
    api: Array<{
      id: number;
      name: string;
      potency: string;
      unit: string;
    }>;
  }) => api.post("/medicines/v2/", data),

  // Document API
  getPatientDocuments: (
    patientId: string,
    params: {
      consolidated?: boolean;
      include_uncategorized?: boolean;
      start_time?: string;
      end_time?: string;
      type?: string;
    } = {}
  ) => {
    const queryParams = new URLSearchParams();
    queryParams.append("consolidated", String(params.consolidated ?? false));
    if (params.include_uncategorized !== undefined) {
      queryParams.append(
        "include_uncategorized",
        String(params.include_uncategorized)
      );
    }
    if (params.start_time) queryParams.append("start_time", params.start_time);
    if (params.end_time) queryParams.append("end_time", params.end_time);
    if (params.type) queryParams.append("type", params.type);

    return api.get(`/patient/${patientId}/document/?${queryParams.toString()}`);
  },

  // Categorize document (also supports updating document_date)
  categorizeDocument: (
    documentId: string,
    documentType: string,
    documentDate?: string
  ) => {
    const payload: { document_type: string; document_date?: string } = {
      document_type: documentType,
    };
    if (documentDate) {
      payload.document_date = documentDate;
    }
    return api.patch(`/patient/document/${documentId}/categorize/`, payload);
  },

  uploadPatientDocument: (
    patientId: string,
    file: File,
    documentType?: string,
    description?: string
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    if (documentType) formData.append("document_type", documentType);
    if (description) formData.append("description", description);
    return api.post(`/patient/${patientId}/document/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  deletePatientDocument: (appointmentId: string, documentId: string) =>
    api.delete(`/patient/${appointmentId}/document/${documentId}/`),

  // Video Call API - POST services/video/token/ with channel payload
  getVideoCallToken: (appointmentId: string) =>
    api.post<{ token: string }>("/services/video/token/", {
      channel: appointmentId,
    }),
  notifyEchoParticipantsReady: (
    appointmentId: string,
    data: {
      channel_name: string;
      provider_uid: string;
      patient_uid: string;
      preferred_language?: string;
    }
  ) => api.post(`/echo/appointments/${appointmentId}/ready/`, data),
  stopEchoRecording: (
    appointmentId: string,
    data?: {
      channel_name?: string;
    }
  ) => api.post(`/echo/appointments/${appointmentId}/stop/`, data || {}),

  // Notify patient to join the call
  notifyPatientToJoin: (appointmentId: string) =>
    api.post(`/services/appointments/${appointmentId}/notify-patient/`),

  // Submit video call feedback
  submitVideoCallFeedback: (
    appointmentId: string,
    data: { rating: number; feedback?: string }
  ) => api.post(`/services/appointments/${appointmentId}/feedback/`, data),

  // Ping API (centralized access)
  // ping: pingAPI,
};

// Document type mapping
export const DOCUMENT_TYPES = {
  UC: "Uncategorized",
  MD: "Medicine",
  LR: "Lab Report",
  PR: "Prescription",
  SC: "Imaging",
  OT: "Others",
} as const;

export type DocumentType = keyof typeof DOCUMENT_TYPES;

// Export the configured axios instance and token manager
export { api, TokenManager, resetRefreshState };
export default api;
